/**
 * Regras das dicas informativas exibidas abaixo dos campos de data e valor
 * do pagamento. Funções puras — a UI só renderiza o que sai daqui.
 */

/**
 * `positive` = pagamento antecipado; `warn` = atraso. Desconto e acréscimo têm tons próprios: são as
 * mesmas cores da coluna "Acrs./Desc." do modal de faturas do pagamento (vermelho e azul).
 */
export type HintTone = 'warn' | 'positive' | 'discount' | 'surcharge';

export interface Hint {
    text: string;
    tone: HintTone;
}

const fmtBRL = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** "YYYY-MM-DD" ou ISO → dias desde a época, ignorando horário e fuso. */
const toDayNumber = (value?: string | null): number | null => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return null;
    const [, year, month, day] = match;
    return Date.UTC(Number(year), Number(month) - 1, Number(day)) / 86_400_000;
};

/**
 * Compara a data do pagamento com o vencimento, ignorando horas.
 * Mesma data → null (sem mensagem).
 */
export const dueDateHint = (paymentDate?: string | null, dueDate?: string | null): Hint | null => {
    const paid = toDayNumber(paymentDate);
    const due = toDayNumber(dueDate);
    if (paid === null || due === null) return null;
    if (paid < due) return { text: 'Pagamento antecipado', tone: 'positive' };
    if (paid > due) return { text: 'Pagamento em Atraso', tone: 'warn' };
    return null;
};

/**
 * Compara o valor informado com o esperado (amount - amount_paid).
 * Abaixo → desconto; acima → acréscimo; igual → null.
 */
export const amountHint = (informed: number, expected: number): Hint | null => {
    if (!Number.isFinite(informed) || !Number.isFinite(expected)) return null;
    const diff = Math.round((informed - expected) * 100) / 100;
    if (diff === 0) return null;
    if (diff < 0) return { text: `Desconto: ${fmtBRL(-diff)}`, tone: 'discount' };
    return { text: `Acréscimo: ${fmtBRL(diff)}`, tone: 'surcharge' };
};

/** Classes do selo (mesmo modelo visual das tags "TODO" da tela). */
export const hintClass = (tone: HintTone): string => {
    const base = 'inline-block text-[10px] px-1.5 py-0.5 rounded';
    const byTone: Record<HintTone, string> = {
        warn: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
        positive: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
        discount: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
        surcharge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    };
    return `${base} ${byTone[tone]}`;
};
